import { useContext, useEffect, useState } from "react";
import { Box, Sheet, Stack, Typography, Chip, LinearProgress, Tooltip } from "@mui/joy";
import { motion } from "framer-motion";

import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';
import TimerRoundedIcon from '@mui/icons-material/TimerRounded';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import FlagRoundedIcon from '@mui/icons-material/FlagRounded';

import { LayoutContext } from "../../context";
import { APIGetQualityDashboardBrief } from "../../api";

const pctHex = (p) => (p >= 80 ? '#16a34a' : p >= 50 ? '#f59e0b' : '#dc2626');

// Компактная плашка показателей качества (мастер по качеству, на главной/календаре)
export default function QualityDashboardBrief() {
    const { tokenEmployee, account } = useContext(LayoutContext);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!tokenEmployee) return;
        const isQM = Array.isArray(account?.position) && account.position.includes('quality_master');
        if (!isQM) return;

        setLoading(true);
        APIGetQualityDashboardBrief(tokenEmployee)
            .then(setData)
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [tokenEmployee, account?.id]);

    if (!data) return null;

    const percent = data.overall_percent || 0;
    const hasGoal = data.has_goal;
    const p = data.progress || {};
    const bonus = data?.bonus_calculation?.total_salary;

    return (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            <Sheet sx={{ borderRadius: 'xl', border: '1px solid', borderColor: 'divider', overflow: 'hidden', bgcolor: 'background.surface', mb: 2, boxShadow: '0 4px 24px rgba(87,42,195,0.06)' }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent={'space-between'} spacing={2}
                    sx={{ p: { xs: 2, sm: 2.5 }, background: 'linear-gradient(135deg, rgba(87,42,195,0.07), rgba(141,106,236,0.03))' }}>
                    <Stack direction={{ xs: 'row' }} alignItems={'center'} spacing={2}>
                        {hasGoal ? (
                            <Box sx={{ position: 'relative', width: 64, height: 64, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <svg width={64} height={64} viewBox="0 0 64 64" style={{ position: 'absolute' }}>
                                    <circle cx="32" cy="32" r="27" fill="none" stroke="#fff" strokeWidth="6" opacity="0.7" />
                                    <circle cx="32" cy="32" r="27" fill="none" stroke={pctHex(percent)} strokeWidth="6" strokeLinecap="round"
                                        strokeDasharray={2 * Math.PI * 27}
                                        strokeDashoffset={(2 * Math.PI * 27) * (1 - Math.min(percent, 100) / 100)}
                                        transform="rotate(-90 32 32)" />
                                </svg>
                                <Typography level="body-sm" sx={{ fontWeight: 800, color: pctHex(percent) }}>{Math.round(percent)}%</Typography>
                            </Box>
                        ) : (
                            <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: 'neutral.softBg', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <FlagRoundedIcon sx={{ color: 'text.tertiary', opacity: 0.5 }} />
                            </Box>
                        )}

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography level="title-md" sx={{ fontWeight: 800 }}>Мои показатели качества</Typography>
                            {hasGoal ? (
                                <Box sx={{ mt: 0.75, maxWidth: 280 }}>
                                    <LinearProgress determinate value={Math.min(percent, 100)} color={percent >= 80 ? 'success' : percent >= 50 ? 'warning' : 'danger'} sx={{ '--LinearProgress-radius': '99px' }} />
                                </Box>
                            ) : (
                                <Typography level="body-sm" sx={{ color: 'text.tertiary', mt: 0.25 }}>Цель на этот месяц не поставлена</Typography>
                            )}
                        </Box>
                    </Stack>

                    <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Chip variant="soft" color="primary" startDecorator={<VerifiedRoundedIcon sx={{ fontSize: 15 }} />} sx={{ fontWeight: 700 }}>
                            В срок: {p.deadline_compliance != null ? `${Math.round(p.deadline_compliance)}%` : '—'}
                        </Chip>
                        <Chip variant="soft" color="warning" startDecorator={<SpeedRoundedIcon sx={{ fontSize: 15 }} />} sx={{ fontWeight: 700 }}>
                            Конверсия: {Math.round(p.funnel_conversion || 0)}%
                        </Chip>
                        <Chip variant="soft" color="neutral" startDecorator={<TimerRoundedIcon sx={{ fontSize: 15 }} />} sx={{ fontWeight: 700 }}>
                            Цикл: {(p.avg_cycle_time_days || 0).toFixed(1)} дн
                        </Chip>
                        {hasGoal && bonus > 0 && (
                            <Tooltip title="Расчётная выплата за месяц" variant="soft">
                                <Chip variant="soft" color="success" startDecorator={<EmojiEventsRoundedIcon sx={{ fontSize: 15 }} />} sx={{ fontWeight: 700 }}>
                                    ≈ {Math.round(bonus).toLocaleString('ru-RU')} ₽
                                </Chip>
                            </Tooltip>
                        )}
                    </Stack>
                </Stack>
                {loading && <LinearProgress color="primary" sx={{ '--LinearProgress-thickness': '2px' }} />}
            </Sheet>
        </motion.div>
    );
}